/**
 * The Peristyle: a colonnaded court on the route. A covered ambulatory runs
 * round all four walls; inside it a rectangle of round columns carries a stone
 * architrave, and the whole court between them is open sky — one big emissive
 * plane instead of the usual inset laylight. A broad, shin-deep basin fills the
 * middle — you wade through it rather than walk round it.
 */

import { rectSegments } from '../hang';

const COL_R = 0.27; // shaft radius at the foot
const COL_TOP = 0.23; // ... and under the capital (a modest entasis)
const PLINTH_H = 0.16;
const CAP_H = 0.26;
const BAY = 2.7; // target column spacing
const ARCH_H = 0.5; // architrave depth
const ARCH_W = 0.24; // architrave half-width

const peristyle = {
  id: 'peristyle',
  role: 'route',
  weight: 2,

  plan(rand, { axis }) {
    const long = 13.5 + rand() * 2.5;
    const cross = 11.2 + rand() * 1.6;
    return {
      w: axis === 'x' ? long : cross,
      d: axis === 'x' ? cross : long,
      height: 7.2,
      params: {
        amb: 2.5 + rand() * 0.45, // ambulatory depth, wall face to column axis
        colH: 4.5 + rand() * 0.3,
        basin: rand() < 0.65,
      },
    };
  },

  // The ambulatory's outer wall is the best wall in the palace: you read it
  // between the columns, one piece per bay, as you walk the covered side. A
  // single eye-level row keeps the colonnade's rhythm rather than fighting it.
  segments(node) {
    return rectSegments(node, { longStyle: 'row', shortStyle: 'row', margin: 1.1 });
  },

  build(node, T) {
    const { WALL_T } = T.PALACE;
    const M = T.kit.materials;
    // `stone` and `water` arrive with this batch of types; fall back to the
    // nearest existing finish so the court still reads if it lands first.
    const stone = M.stone || M.marble;
    const water = M.water || M.laylight;

    const p = node.params || {};
    const amb = p.amb ?? 2.7;
    const colH = p.colH ?? 4.6;

    const xI1 = node.x1 + WALL_T;
    const xI2 = node.x2 - WALL_T;
    const zI1 = node.z1 + WALL_T;
    const zI2 = node.z2 - WALL_T;
    const c1x = xI1 + amb; // column axes
    const c2x = xI2 - amb;
    const c1z = zI1 + amb;
    const c2z = zI2 - amb;
    const courtW = c2x - c1x;
    const courtD = c2z - c1z;
    const cx = (c1x + c2x) / 2;
    const cz = (c1z + c2z) / 2;

    // The shell, with the "sky" filling the whole court rather than the
    // default inset panel. Its coffer beams land just inside the colonnade.
    const { group, colliders } = T.standardShell(node, T.kit, {
      wallMaterial: T.kit.wallFor(node.palette || 0, true),
      floorMaterial: M.marble,
      laylight: { w: Math.max(2, courtW - 0.5), d: Math.max(2, courtD - 0.5) },
    });

    // Court paving, a shade apart from the ambulatory and a hair proud of it.
    const stoneGeos = [T.box(c1x, c2x, -0.02, 0.015, c1z, c2z)];

    // --- the colonnade: a rectangle of columns, corners shared.
    const nx = Math.max(2, Math.round(courtW / BAY));
    const nz = Math.max(2, Math.round(courtD / BAY));
    const shaftY0 = PLINTH_H - 0.02;
    const shaftY1 = colH - CAP_H + 0.03;
    // A column standing on a door's axis would meet the visitor head on; drop
    // it and let that bay run wide, the way an entrance bay always does.
    const onDoorAxis = (x, z) => (node.doors || []).some((d) => {
      const horizontal = d.side === 'N' || d.side === 'S';
      const clear = (d.w || T.PALACE.DOOR_W) / 2 + 0.85;
      const wall = d.side === 'N' ? zI2 : d.side === 'S' ? zI1 : d.side === 'E' ? xI2 : xI1;
      const off = Math.abs((horizontal ? x : z) - d.at);
      const deep = Math.abs((horizontal ? z : x) - wall);
      return off < clear && deep < amb + 0.5;
    });
    for (let i = 0; i <= nx; i++) {
      for (let j = 0; j <= nz; j++) {
        if (i > 0 && i < nx && j > 0 && j < nz) continue;
        const x = c1x + (courtW * i) / nx;
        const z = c1z + (courtD * j) / nz;
        if (onDoorAxis(x, z)) continue;
        const shaft = new T.THREE.CylinderGeometry(COL_TOP, COL_R, shaftY1 - shaftY0, 14);
        shaft.translate(x, (shaftY0 + shaftY1) / 2, z);
        stoneGeos.push(
          shaft,
          T.box(x - 0.34, x + 0.34, 0, PLINTH_H, z - 0.34, z + 0.34),
          T.box(x - 0.31, x + 0.31, colH - CAP_H, colH, z - 0.31, z + 0.31)
        );
        colliders.push({ circle: [x, z, 0.38], top: colH });
      }
    }

    // --- architrave over the colonnade, the four runs crossing at the corners.
    const beamY1 = colH + ARCH_H;
    stoneGeos.push(
      T.box(c1x - 0.34, c2x + 0.34, colH, beamY1, c1z - ARCH_W, c1z + ARCH_W),
      T.box(c1x - 0.34, c2x + 0.34, colH, beamY1, c2z - ARCH_W, c2z + ARCH_W),
      T.box(c1x - ARCH_W, c1x + ARCH_W, colH, beamY1, c1z - 0.34, c2z + 0.34),
      T.box(c2x - ARCH_W, c2x + ARCH_W, colH, beamY1, c1z - 0.34, c2z + 0.34)
    );

    // --- basin: a stone kerb round a still sheet of water.
    if (p.basin) {
      // Wide enough to be the court's floor rather than an ornament in it —
      // it's shin-high and you wade straight through it.
      const bw = Math.min(6.6, courtW * 0.62);
      const bd = Math.min(4.6, courtD * 0.56);
      const b1x = cx - bw / 2;
      const b2x = cx + bw / 2;
      const b1z = cz - bd / 2;
      const b2z = cz + bd / 2;
      stoneGeos.push(
        T.box(b1x, b2x, 0, 0.38, b1z, b1z + 0.3),
        T.box(b1x, b2x, 0, 0.38, b2z - 0.3, b2z),
        T.box(b1x, b1x + 0.3, 0, 0.38, b1z + 0.3, b2z - 0.3),
        T.box(b2x - 0.3, b2x, 0, 0.38, b1z + 0.3, b2z - 0.3)
      );
      group.add(T.mergedMesh(
        [T.box(b1x + 0.24, b2x - 0.24, 0, 0.34, b1z + 0.24, b2z - 0.24)],
        water
      ));
      colliders.push({ rect: [b1x, b1z, b2x, b2z], top: 0.42, soft: true });
    }

    group.add(T.mergedMesh(stoneGeos, stone));

    // A pale fascia capping the architrave, tying the order to the wall trim.
    const F = 0.07;
    group.add(T.mergedMesh([
      T.box(c1x - 0.34 - F, c2x + 0.34 + F, beamY1 - 0.02, beamY1 + 0.16, c1z - ARCH_W - F, c1z + ARCH_W + F),
      T.box(c1x - 0.34 - F, c2x + 0.34 + F, beamY1 - 0.02, beamY1 + 0.16, c2z - ARCH_W - F, c2z + ARCH_W + F),
      T.box(c1x - ARCH_W - F, c1x + ARCH_W + F, beamY1 - 0.02, beamY1 + 0.16, c1z - 0.34 - F, c2z + 0.34 + F),
      T.box(c2x - ARCH_W - F, c2x + ARCH_W + F, beamY1 - 0.02, beamY1 + 0.16, c1z - 0.34 - F, c2z + 0.34 + F),
    ], M.trim));

    // Benches against the ambulatory's outer wall, skipped where a door on
    // that side would have to walk through them.
    for (const s of [-1, 1]) {
      const bx = cx + s * courtW * 0.3;
      const blocked = (node.doors || []).some((d) =>
        d.side === 'S' && Math.abs(d.at - bx) < (d.w || T.PALACE.DOOR_W) / 2 + 1.3);
      if (blocked) continue;
      const b = T.bench(T.kit, bx, zI1 + 0.4, 0, 2.2);
      group.add(b.mesh);
      colliders.push(b.collider);
    }

    return { group, colliders };
  },
};

export default peristyle;
